// DocSignList.jsx
import React, { useEffect, useState } from "react"
import {
    Card,
    CardContent,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Stack,
    CardHeader,
    Checkbox,
    Menu,
    MenuItem,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Chip,
    Tooltip,
    Box,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import moment from "moment"

import { api } from "../../../services/Main/Api"
import { getCondoId } from "../../../services/Main/AuthStorage"
import PaginationPage from "../../../components/PaginationPage/PaginationPage"

import { useSnackbar } from "notistack"
import TemplateModalForm from "./TemplateForm"
import { useNavigate } from "react-router-dom"

const DocSignList = () => {
    const navigate = useNavigate()
    const [templates, setTemplates] = useState([])
    const [selectedIds, setSelectedIds] = useState([])
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [anchorEl, setAnchorEl] = useState(null)
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [dialogOpen, setDialogOpen] = useState(false)
    const { enqueueSnackbar } = useSnackbar()
    const openMenu = Boolean(anchorEl)

    const results = templates?.results || []
    const count = templates?.count || 0
    const isEmpty = !loading && results.length === 0

    const goToDetail = (id) => {
        if (!id) return
        navigate(`/assinatura/${id}`)
    }

    useEffect(() => {
        setLoading(true)
        api
            .get("sign/doc-sign/", {
                params: { condo_id: getCondoId(), page, page_size: 50 },
            })
            .then((res) => {
                setTemplates(res.data)
                setSelectedIds([])
            })
            .finally(() => setLoading(false))
    }, [page])

    const handleSelectAll = (event) => {
        const all = results.map((t) => t.id)
        setSelectedIds(event.target.checked ? all : [])
    }

    const handleSelectOne = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]))
    }

    const isSelected = (id) => selectedIds.includes(id)
    const allSelected = results.length > 0 && selectedIds.length === results.length

    const handleMenuClick = (event) => setAnchorEl(event.currentTarget)
    const handleMenuClose = () => setAnchorEl(null)
    const handleDeleteClick = () => {
        setConfirmOpen(true)
        handleMenuClose()
    }

    const handleDeleteConfirmed = async () => {
        try {
            await Promise.all(selectedIds.map((id) => api.delete(`sign/doc-sign/${id}/`)))
            setTemplates((prev) => ({
                ...prev,
                results: (prev.results || []).filter((t) => !selectedIds.includes(t.id)),
                count: (prev.count || 0) - selectedIds.length,
            }))
            enqueueSnackbar("Assinatura(s) deletada(s) com sucesso!", { variant: "success" })
        } catch (error) {
            console.error(error)
            enqueueSnackbar("Erro ao deletar assinaturas", { variant: "error" })
        } finally {
            setSelectedIds([])
            setConfirmOpen(false)
        }
    }

    const handleSubmitNewTemplate = async (formData) => {
        try {
            const res = await api.post("sign/doc-sign/", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            })
            setTemplates((prev) => ({
                ...prev,
                results: [...(prev.results || []), res.data],
                count: (prev.count || 0) + 1,
            }))
            enqueueSnackbar("Assinatura criada com sucesso!", { variant: "success" })
            setDialogOpen(false)
        } catch (error) {
            console.error(error)
            enqueueSnackbar("Erro ao criar assinatura", { variant: "error" })
        }
    }

    return (
        <>
            <Card sx={{ m: 2 }}>
                <CardHeader title={`Assinaturas (${count})`} />
                <Divider sx={{ mb: 2 }} />

                {/* Ações do topo */}
                <Stack direction="row" spacing={2} alignItems="center" sx={{ px: 2, pb: 1 }}>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        href={"/assinatura/enviar-documento"}
                    >
                        Enviar Documento
                    </Button>

                    {!isEmpty && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<KeyboardArrowDownIcon />}
                            onClick={handleMenuClick}
                            disabled={selectedIds.length === 0}
                        >
                            Ações {selectedIds.length > 0 ? `(${selectedIds.length})` : ""}
                        </Button>
                    )}

                    <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
                        <MenuItem onClick={handleDeleteClick}>
                            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                            Excluir selecionados
                        </MenuItem>
                    </Menu>
                </Stack>

                <CardContent>
                    {loading ? (
                        <Typography variant="body2">Carregando assinaturas...</Typography>
                    ) : isEmpty ? (
                        <Box
                            sx={{
                                py: 6,
                                px: 2,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                textAlign: "center",
                                border: (theme) => `1px dashed ${theme.palette.divider}`,
                                borderRadius: 2,
                                bgcolor: (theme) => theme.palette.action.hover,
                            }}
                        >
                            <Typography variant="h6" sx={{ mb: 0.5 }}>
                                Nenhuma assinatura ainda
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, mb: 2 }}>
                                Para começar a enviar contratos para assinatura, você precisa cadastrar um{" "}
                                <strong>modelo de contrato (Docx)</strong>.
                            </Typography>
                            <Button variant="contained" startIcon={<AddIcon />} href={"/modelo"}>
                                Configurar modelo de contrato
                            </Button>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox checked={allSelected} onChange={handleSelectAll} />
                                        </TableCell>
                                        <TableCell>
                                            <strong>Nome do contrato</strong>
                                        </TableCell>
                                        <TableCell align="center">
                                            <strong>Status</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Criado em</strong>
                                        </TableCell>
                                        <TableCell>
                                            <strong>Assinaturas</strong>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {results.map((template) => (
                                        <TableRow
                                            key={template.id}
                                            hover
                                            onClick={() => goToDetail(template.id)}
                                            sx={{ cursor: "pointer" }}
                                        >
                                            <TableCell
                                                padding="checkbox"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Checkbox
                                                    checked={isSelected(template.id)}
                                                    onChange={() => handleSelectOne(template.id)}
                                                />
                                            </TableCell>
                                            <TableCell>{template.name}</TableCell>
                                            <TableCell align="center">
                                                {template.status?.value === "FINISHED" && (
                                                    <Chip size="small" variant="outlined" color="success" label={template.status?.label} />
                                                )}
                                                {template.status?.value === "DECLINED" && (
                                                    <Chip size="small" variant="outlined" color="error" label={template.status?.label} />
                                                )}
                                                {template.status?.value === "PENDING" && (
                                                    <Chip size="small" variant="outlined" color="secondary" label={template.status?.label} />
                                                )}
                                                {template.status?.value === "SENT" && (
                                                    <Chip size="small" variant="outlined" color="warning" label={template.status?.label} />
                                                )}
                                                {!template.status?.value && (
                                                    <Chip size="small" variant="outlined" label="-" />
                                                )}
                                            </TableCell>

                                            <TableCell>
                                                {template.created_at
                                                    ? moment(template.created_at).format("DD/MM/YYYY [às] HH:mm")
                                                    : "-"}
                                            </TableCell>

                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <Tooltip
                                                    title={
                                                        <Box>
                                                            <Typography variant="h6">Assinaturas pendentes</Typography>
                                                            <Divider sx={{ mb: 1 }} />
                                                            {(template?.pending_signers || []).length === 0 ? (
                                                                <Typography variant="caption">Nenhum pendente</Typography>
                                                            ) : (
                                                                (template?.pending_signers || []).map((signer, i) => (
                                                                    <Box key={`${template.id}-${i}`} sx={{ py: 0.5 }}>
                                                                        <Typography variant="caption">
                                                                            {signer.name} — {signer.email}
                                                                        </Typography>
                                                                    </Box>
                                                                ))
                                                            )}
                                                        </Box>
                                                    }
                                                >
                                                    <Chip
                                                        size="small"
                                                        label={template?.signed || "-"}
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            goToDetail(template.id)
                                                        }}
                                                        sx={{ cursor: "pointer" }}
                                                    />
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            {!isEmpty && <PaginationPage data={{ ...templates, page }} onChange={setPage} />}

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <Typography>
                        Deseja realmente deletar {selectedIds.length} assinatura{selectedIds.length > 1 ? "s" : ""}?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
                    <Button onClick={handleDeleteConfirmed} color="error" variant="contained">
                        Deletar
                    </Button>
                </DialogActions>
            </Dialog>

            <TemplateModalForm
                isOpen={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSubmit={handleSubmitNewTemplate}
            />
        </>
    )
}

export default DocSignList
